import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ColumnResizeHandle, useColumnWidths } from '@/tableControls'
import type { HistoryMonths, PickerDriverType } from '@/store/projectionsStore'
import { buildGridRows, filterCollapsedRows } from './buildGridRows'
import { policyOwnedLineKeys } from './engine/cashPolicy'
import type { CaseInputs, DriverParams, History, Model, SchemaEntry, View } from './engine/types'
import GridRow from './GridRow'
import ApplyRecipeConfirm from './ApplyRecipeConfirm'
import { explainCell } from './explain/explainCell'
import ExplainPopover from './explain/ExplainPopover'
import { formatDriverInput } from './driverUtils'
import { monthKeyFromT } from './engine/calendar'
import { COMPARE_CHIP, COMPARE_CHIP_ON, PROJ_EDGE, STICKY_DRIVER, W_MONTH, rowHeight, type PickerDriver } from './gridClasses'
import { formatMonthLabel } from './projectionsFormat'
import { getVisibleTimeIndices, monthLabel, monthOffsetLabel } from './monthColumns'
import type { CompareCell, CompareModel } from './compare/compareModel'

export type ProjectionsGridCompare = {
  model: CompareModel
  columns: 'all' | string[]
  caseName: string
  onToggleColumn: (month: string) => void
  sourceByMonth: Record<string, { caseSavedAt: string | null; capturedAt: string | null }>
}

export type ProjectionsGridHandle = {
  scrollToRowIndex: (index: number) => void
}

type ProjectionsGridProps = {
  schema: SchemaEntry[]
  view: View
  draft: CaseInputs
  evaluated: Model | null
  history: History | null
  historyMonths: HistoryMonths
  lastClosedMonth: string
  canEdit: boolean
  onSetDriverType: (line: string, type: PickerDriverType) => void
  onSetDriverParams: (line: string, type: PickerDriver, params: DriverParams) => void
  onSetPctRef: (line: string, refKey: string) => void
  onSetValue: (line: string, t: number, value: number | null) => void
  onApplyFromColumn: (
    line: string,
    startT: number,
    mode?: 'keep_shape' | 'replace_flat',
  ) => void
  onClearOverride?: (line: string, t: number) => void
  onSetOverrideReason?: (line: string, t: number, reason: string | null) => void
  onClearDriverValues: (line: string) => void
  onToggleConsolidatedOnly: (line: string) => void
  notes?: Record<string, string>
  onOpenNote?: import('./GridRow').OnOpenNote
  collapsedSections?: ReadonlySet<string>
  onToggleSection?: (sectionKey: string) => void
  partnerLabels?: Record<string, string> | null
  /** Test-only: render every row without virtualizing. */
  disableVirtualization?: boolean
  compare?: ProjectionsGridCompare | null
}

const EMPTY_POLICY_OWNED = new Set<string>()

type ExplainTarget = {
  lineKey: string
  t: number
  anchor: DOMRect | { x: number; y: number }
}

type PendingApply = {
  lineKey: string
  startT: number
}

const ProjectionsGrid = forwardRef<ProjectionsGridHandle, ProjectionsGridProps>(
  function ProjectionsGrid(
    {
      schema,
      view,
      draft,
      evaluated,
      history,
      historyMonths,
      lastClosedMonth,
      canEdit,
      onSetDriverType,
      onSetDriverParams,
      onSetPctRef,
      onSetValue,
      onApplyFromColumn,
      onClearOverride,
      onSetOverrideReason,
      onClearDriverValues,
      onToggleConsolidatedOnly,
      notes = {},
      onOpenNote,
      collapsedSections = new Set(),
      onToggleSection,
      partnerLabels = null,
      disableVirtualization = false,
      compare = null,
    },
    ref,
  ) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [explainTarget, setExplainTarget] = useState<ExplainTarget | null>(null)
    const [pendingApply, setPendingApply] = useState<PendingApply | null>(null)
    const { widths, startResize } = useColumnWidths({
      pageKey: 'projections',
      columnKeys: ['line_item', 'driver', 'month'],
      defaults: { line_item: 280, driver: 180, month: W_MONTH },
    })

    const lineWidth = widths.line_item ?? 280
    const driverWidth = widths.driver ?? 180
    const monthWidth = widths.month ?? W_MONTH
    const driverLeft = lineWidth

    const schemaByKey = useMemo(() => new Map(schema.map((entry) => [entry.key, entry])), [schema])
    const policyOwnedKeys = useMemo(
      () => (draft.cash_policy?.enabled ? policyOwnedLineKeys(schema) : EMPTY_POLICY_OWNED),
      [draft.cash_policy?.enabled, schema],
    )
    const splitScheduleComputedAt =
      draft.cash_policy?.distribution.split_schedule?.computed_at ?? null
    const rows = useMemo(() => {
      const built = buildGridRows(schema, view, draft, {
        policyOwnedKeys,
        splitScheduleComputedAt,
      })
      return filterCollapsedRows(built, collapsedSections)
    }, [schema, view, draft, collapsedSections, policyOwnedKeys, splitScheduleComputedAt])
    const { histTs, projTs } = useMemo(
      () => getVisibleTimeIndices(history?.nHistory ?? 0, historyMonths),
      [history?.nHistory, historyMonths],
    )

    const compareCellsByT = useMemo(() => {
      const map = new Map<number, Record<string, CompareCell>>()
      if (!compare) return map
      for (const t of histTs) {
        const month = monthKeyFromT(lastClosedMonth, t)
        const cells = compare.model.byMonth[month]?.[view]
        if (cells) map.set(t, cells)
      }
      return map
    }, [compare, histTs, lastClosedMonth, view])

    const tableWidth = lineWidth + driverWidth + (histTs.length + projTs.length) * monthWidth

    const explainModel = useMemo(() => {
      if (!explainTarget || !history) {
        return null
      }
      return explainCell({
        view,
        lineKey: explainTarget.lineKey,
        t: explainTarget.t,
        schema,
        inputs: draft,
        history,
        evaluated,
        notes,
        lastClosedMonth,
      })
    }, [
      draft,
      evaluated,
      explainTarget,
      history,
      lastClosedMonth,
      notes,
      schema,
      view,
    ])

    const handleOpenExplain = useCallback(
      (lineKey: string, t: number, anchor: DOMRect | { x: number; y: number }) => {
        setExplainTarget({ lineKey, t, anchor })
      },
      [],
    )

    const handleCloseExplain = useCallback(() => {
      setExplainTarget(null)
    }, [])

    const rowVirtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: (index) => rowHeight(rows[index]?.kind ?? 'computed'),
      overscan: 8,
      getItemKey: (index) => {
        const row = rows[index]
        if (!row) return index
        if (row.kind === 'band') return `band-${row.sectionKey}`
        if (row.kind === 'sectionPad') return `pad-${row.sectionKey}`
        if (row.kind === 'gap') return `gap-${index}`
        if (row.kind === 'group') return `group-${row.key}`
        return row.key
      },
    })

    useImperativeHandle(ref, () => ({
      scrollToRowIndex: (index: number) => {
        rowVirtualizer.scrollToIndex(index, { align: 'start' })
      },
    }))

    const virtualItems = disableVirtualization
      ? rows.map((_, index) => ({ index, start: 0, end: rowHeight(rows[index]?.kind ?? 'computed') }))
      : rowVirtualizer.getVirtualItems()
    const paddingTop = disableVirtualization ? 0 : virtualItems[0]?.start ?? 0
    const paddingBottom = disableVirtualization
      ? 0
      : rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)

    const historyAdapter = history
      ? { nHistory: history.nHistory, values: history.values, laborMeta: history.laborMeta }
      : null

    const handleSetDriverType = useCallback(
      (line: string, type: PickerDriverType) => onSetDriverType(line, type),
      [onSetDriverType],
    )
    const handleSetDriverParams = useCallback(
      (line: string, type: PickerDriver, params: DriverParams) =>
        onSetDriverParams(line, type, params),
      [onSetDriverParams],
    )
    const handleSetPctRef = useCallback(
      (line: string, refKey: string) => onSetPctRef(line, refKey),
      [onSetPctRef],
    )
    const handleSetValue = useCallback(
      (line: string, t: number, value: number | null) => onSetValue(line, t, value),
      [onSetValue],
    )
    const handleApplyFromColumn = useCallback(
      (line: string, startT: number, mode?: 'keep_shape' | 'replace_flat') => {
        if (mode) {
          onApplyFromColumn(line, startT, mode)
          return
        }
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        const recipe = active ? lineState?.by_type[active]?.recipe : null
        if (recipe) {
          setPendingApply({ lineKey: line, startT })
          return
        }
        onApplyFromColumn(line, startT)
      },
      [draft.drivers, onApplyFromColumn, view],
    )

    const pendingApplyDetails = useMemo(() => {
      if (!pendingApply) {
        return null
      }
      const lineState = draft.drivers[view]?.[pendingApply.lineKey]
      const active = lineState?.active as PickerDriver | null
      if (!active) {
        return null
      }
      const values = lineState?.by_type[active]?.values
      const sourceVal = values?.[pendingApply.startT - 2] ?? null
      const sourceMonth = monthKeyFromT(lastClosedMonth, pendingApply.startT - 1)
      return {
        sourceMonthLabel: formatMonthLabel(sourceMonth),
        formattedValue: formatDriverInput(active, sourceVal),
      }
    }, [draft.drivers, lastClosedMonth, pendingApply, view])
    const handleClearDriverValues = useCallback(
      (line: string) => onClearDriverValues(line),
      [onClearDriverValues],
    )
    const handleToggleConsolidatedOnly = useCallback(
      (line: string) => onToggleConsolidatedOnly(line),
      [onToggleConsolidatedOnly],
    )

    return (
      <>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-100">
        <table
          className="table-forecast border-separate border-spacing-0"
          style={{ tableLayout: 'fixed', width: tableWidth, minWidth: tableWidth }}
        >
          <colgroup>
            <col style={{ width: lineWidth }} />
            <col style={{ width: driverWidth }} />
            {[...histTs, ...projTs].map((t) => (
              <col key={t} style={{ width: monthWidth }} />
            ))}
          </colgroup>
          <thead>
            <tr className="h-6">
              <th
                colSpan={2}
                className="table-locked-edge sticky left-0 top-0 z-40 bg-gray-50 !px-2 !py-0"
                scope="col"
              />
              <th
                colSpan={histTs.length}
                className="sticky top-0 z-30 bg-gray-50 !px-2 !py-0 text-xs text-gray-600"
                scope="col"
              >
                {compare
                  ? `Actuals — Δ vs ${compare.caseName} projection`
                  : 'Actuals'}
              </th>
              <th
                colSpan={projTs.length}
                className={`sticky top-0 z-30 bg-gray-50 !px-2 !py-0 text-xs text-gray-600 ${PROJ_EDGE}`}
                scope="col"
              >
                Projected
              </th>
            </tr>
            <tr className={compare ? 'h-auto min-h-9' : 'h-9'}>
              <th
                className="sticky left-0 top-6 z-40 border-b border-gray-200 bg-gray-50 !px-2 !py-1"
                scope="col"
              >
                Line item
                <ColumnResizeHandle columnKey="line_item" onStartResize={startResize} />
              </th>
              <th
                className={`${STICKY_DRIVER} sticky top-6 z-40 border-b border-gray-200 bg-gray-50 !px-2 !py-1`}
                style={{ left: driverLeft }}
                scope="col"
              >
                Driver
                <ColumnResizeHandle columnKey="driver" onStartResize={startResize} />
              </th>
              {histTs.map((t) => {
                const month = monthKeyFromT(lastClosedMonth, t)
                const showChip = compare != null && compare.model.months.includes(month)
                const pinned =
                  compare != null &&
                  (compare.columns === 'all' || compare.columns.includes(month))
                return (
                <th
                  key={`h-${t}`}
                  className="sticky top-6 z-30 border-b border-r border-gray-200 bg-gray-50 !px-2 !py-1 !text-right text-gray-600 tabular-nums"
                  scope="col"
                >
                  <span className="block text-[10px] font-normal normal-case tracking-normal">
                    {monthOffsetLabel(t)}
                  </span>
                  <span className="block">{monthLabel(lastClosedMonth, t)}</span>
                  {showChip ? (
                    <button
                      type="button"
                      className={pinned ? COMPARE_CHIP_ON : COMPARE_CHIP}
                      aria-pressed={pinned}
                      aria-label={`Toggle projection compare for ${month}`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        compare.onToggleColumn(month)
                      }}
                    >
                      ◆ proj
                    </button>
                  ) : null}
                  <ColumnResizeHandle columnKey="month" onStartResize={startResize} />
                </th>
                )
              })}
              {projTs.map((t, i) => (
                <th
                  key={`p-${t}`}
                  className={`sticky top-6 z-30 border-b border-b-gray-300 bg-gray-50 !px-2 !py-1 !text-right text-gray-600 tabular-nums ${i === 0 ? PROJ_EDGE : ''}`}
                  scope="col"
                >
                  <span className="block text-[10px] font-normal normal-case tracking-normal">
                    {monthOffsetLabel(t)}
                  </span>
                  <span className="block">{monthLabel(lastClosedMonth, t)}</span>
                  <ColumnResizeHandle columnKey="month" onStartResize={startResize} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paddingTop > 0 ? (
              <tr className="proj-virtual-pad">
                <td
                  colSpan={2 + histTs.length + projTs.length}
                  style={{ height: paddingTop, padding: 0 }}
                />
              </tr>
            ) : null}
            {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index]
              if (!row) return null
              return (
                <GridRow
                  key={rowVirtualizer.options.getItemKey?.(virtualRow.index) ?? virtualRow.index}
                  ref={disableVirtualization ? undefined : rowVirtualizer.measureElement}
                  data-index={disableVirtualization ? undefined : virtualRow.index}
                  row={row}
                  view={view}
                  schema={schema}
                  schemaByKey={schemaByKey}
                  draft={draft}
                  evaluated={evaluated}
                  histTs={histTs}
                  projTs={projTs}
                  history={historyAdapter}
                  lastClosedMonth={lastClosedMonth}
                  canEdit={canEdit}
                  driverLeft={driverLeft}
                  histMonthCount={histTs.length}
                  projMonthCount={projTs.length}
                  onSetDriverType={handleSetDriverType}
                  onSetDriverParams={handleSetDriverParams}
                  onSetPctRef={handleSetPctRef}
                  onSetValue={handleSetValue}
                  onApplyFromColumn={handleApplyFromColumn}
                  onClearDriverValues={handleClearDriverValues}
                  onToggleConsolidatedOnly={handleToggleConsolidatedOnly}
                  notes={notes}
                  onOpenNote={onOpenNote}
                  onOpenExplain={handleOpenExplain}
                  collapsedSections={collapsedSections}
                  onToggleSection={onToggleSection}
                  partnerLabels={partnerLabels}
                  compare={compare}
                  compareCellsByT={compareCellsByT}
                />
              )
            })}
            {paddingBottom > 0 ? (
              <tr className="proj-virtual-pad">
                <td
                  colSpan={2 + histTs.length + projTs.length}
                  style={{ height: paddingBottom, padding: 0 }}
                />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {explainModel && explainTarget ? (
        <ExplainPopover
          model={explainModel}
          anchor={explainTarget.anchor}
          onClose={handleCloseExplain}
          canEdit={canEdit}
          onSetOverrideReason={
            onSetOverrideReason
              ? (reason) => onSetOverrideReason(explainTarget.lineKey, explainTarget.t, reason)
              : undefined
          }
          onClearOverride={
            onClearOverride
              ? () => {
                  onClearOverride(explainTarget.lineKey, explainTarget.t)
                  handleCloseExplain()
                }
              : undefined
          }
        />
      ) : null}
      {pendingApply && pendingApplyDetails ? (
        <ApplyRecipeConfirm
          sourceMonthLabel={pendingApplyDetails.sourceMonthLabel}
          formattedValue={pendingApplyDetails.formattedValue}
          onCancel={() => setPendingApply(null)}
          onKeepShape={() => {
            onApplyFromColumn(pendingApply.lineKey, pendingApply.startT, 'keep_shape')
            setPendingApply(null)
          }}
          onReplaceFlat={() => {
            onApplyFromColumn(pendingApply.lineKey, pendingApply.startT, 'replace_flat')
            setPendingApply(null)
          }}
        />
      ) : null}
      </>
    )
  },
)

export default ProjectionsGrid
